/**
 * TextInput - Shared component for pasting text content for analysis
 *
 * Features:
 * - Large textarea for text input (Coss Textarea primitive)
 * - Optional title field (Coss Input primitive)
 * - Character count display
 * - Validation (non-empty, max 50KB)
 * - Visual feedback for validation status
 *
 * Used by: Upload page (alternative to file upload)
 *
 * Migration (pirx-4568j): the hand-rolled <input>/<textarea> pair was replaced
 * with the Coss Input/Textarea primitives to keep visual styling consistent
 * with the rest of the migrated UI surface (rounded-lg). Validation borders
 * (destructive / primary / input) are applied via Tailwind arbitrary variants
 * on the wrapper instead of the legacy custom border classes — the underlying
 * element still respects them because the wrapper carries the data-slot hook.
 *
 * The data-test selectors (text-title-input, text-content-input, text-char-count)
 * are preserved for E2E + frontend tests.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Info } from "lucide-react";
import { useUnsavedChanges } from "@/services/app-update/unsaved-changes";

// Validation constants (must match backend)
import type * as React from "react";
const MAX_TEXT_LENGTH = 50000; // 50,000 characters (excluding whitespace)

export interface TextInputData {
  text: string;
  title?: string;
}

interface TextInputProps {
  onTextChange: (data: TextInputData | null) => void;
  onError?: (error: string | null) => void;
  disabled?: boolean;
  creditUnitChars?: number;
  translationNamespace?: string;
  className?: string;
  errorId?: string;
}

/**
 * Wrapper-state classes that drive the textarea border color from validation
 * state. Mirrors the legacy visual (destructive on error, primary when valid,
 * input otherwise) but keyed off the new Coss wrapper tailwind selectors so
 * it lands on the right element after migration.
 */
const TEXTAREA_WRAPPER_STATE =
  "has-aria-invalid:border-destructive has-aria-invalid:focus-within:border-destructive " +
  "data-[valid=true]:border-primary data-[valid=true]:focus-within:border-primary";

export function TextInput({
  onTextChange,
  onError,
  disabled = false,
  creditUnitChars = 1000,
  translationNamespace = "upload",
  className = "",
  errorId,
}: TextInputProps) {
  const { t } = useTranslation([translationNamespace]);
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  useUnsavedChanges(text !== "" || title !== "");

  // Calculate character count excluding whitespace (matches billing calculation)
  const characterCountExcludingWhitespace = useMemo(() => {
    return text.replace(/\s/g, "").length;
  }, [text]);

  const validation = useMemo(() => {
    if (text.length === 0) {
      return { valid: false, error: null };
    }
    if (characterCountExcludingWhitespace > MAX_TEXT_LENGTH) {
      return { valid: false, error: t("textInput.errors.tooLong") };
    }
    return { valid: true, error: null };
  }, [text, characterCountExcludingWhitespace, t]);

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newText = e.target.value;
      setText(newText);

      // Count characters excluding whitespace (matches billing)
      const newCharCount = newText.replace(/\s/g, "").length;
      const isTooLong = newCharCount > MAX_TEXT_LENGTH;
      const isEmpty = newText.length === 0;

      if (isEmpty) {
        onTextChange(null);
        onError?.(null);
        return;
      }

      if (isTooLong) {
        onTextChange(null);
        onError?.(t("textInput.errors.tooLong"));
        return;
      }

      onTextChange({ text: newText, title: title || undefined });
      onError?.(null);
    },
    [title, onTextChange, onError, t],
  );

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTitle = e.target.value;
      setTitle(newTitle);

      if (validation.valid) {
        onTextChange({ text, title: newTitle || undefined });
      }
    },
    [text, validation.valid, onTextChange],
  );

  const charCountColor = useMemo(() => {
    if (text.length === 0) return "text-[var(--muted-foreground)]";
    if (characterCountExcludingWhitespace > MAX_TEXT_LENGTH) {
      return "text-[var(--destructive)]";
    }
    return "text-[var(--primary)]";
  }, [text.length, characterCountExcludingWhitespace]);

  return (
    <div className={`w-full space-y-4 ${className}`}>
      {/* Optional title input */}
      <div>
        <label
          htmlFor="text-title"
          className="mb-1 block text-sm font-medium text-[var(--foreground)]"
        >
          {t("textInput.title")}
        </label>
        <Input
          id="text-title"
          type="text"
          value={title}
          onChange={handleTitleChange}
          disabled={disabled}
          placeholder={t("textInput.titlePlaceholder")}
          data-test="text-title-input"
          nativeInput
        />
      </div>

      {/* Text area */}
      <div>
        <label
          htmlFor="text-content"
          className="mb-1 block text-sm font-medium text-[var(--foreground)]"
        >
          {t("textInput.contentLabel")}
        </label>
        <Textarea
          id="text-content"
          value={text}
          onChange={handleTextChange}
          disabled={disabled}
          placeholder={t("textInput.placeholder")}
          data-test="text-content-input"
          rows={12}
          aria-invalid={validation.error ? true : undefined}
          aria-describedby={validation.error ? errorId : undefined}
          className={TEXTAREA_WRAPPER_STATE}
          data-valid={validation.valid ? "true" : undefined}
        />
      </div>

      {/* Character count and validation info */}
      <div className="mb-3 flex items-center justify-between text-xs">
        <div className="flex items-center text-[var(--muted-foreground)]">
          <span>{t("textInput.lengthHint")}</span>
        </div>
        <span className={charCountColor} data-test="text-char-count">
          {t("textInput.charCount", { count: text.length })}
        </span>
      </div>

      {/* Reminder */}
      <div className="mb-2 flex items-start gap-2 rounded-[var(--r-lg)] border border-[var(--border)] bg-[var(--accent)] px-3 py-3 text-xs text-[var(--accent-foreground)]">
        <Info className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        <span>{t("textInput.reminder", { creditUnitChars })}</span>
      </div>
    </div>
  );
}
